'use client'
/***
 *
 * @file components/ImageWrapper/index.tsx
 * @description ImageWrapper component will be used for rendering the content.
 * @author Naveen Kumar
 * @organization Truminds Software System
 * @since 13 Feb 2024
 *
 */

import React, { ImgHTMLAttributes, memo, useEffect, useState } from "react";
// import "./ImageWrapper.scss";
import defImage from '@/assets/images/defaults/default-image.jpg';
// import Image from "next/legacy/image";
import Image, { ImageLoader, ImageLoaderProps } from "next/image";
import { useResponsive } from "@/client/hooks/useResponsive";
import { getMediaUrl, normalizeContentfulUrl, srcPath } from "@/helpers/media.helper";
import classNames from "classnames";
import { StaticImport } from "next/dist/shared/lib/get-img-props";
// import { customImageLoader } from "@/services/image.service";

/** defines type of props for ImageWrapper component */
type T_Props = ImgHTMLAttributes<HTMLImageElement> & {
  /** fallback image in case actual image fails to render */
  defaultImage?: any,

  /** CSS styles to be provided to the Image component in ImageWrapper */
  style?: any,

  /** image size to be provided to the Image component in ImageWrapper */
  sizes?: any,

  /** image width */
  width?: number | undefined,

  /** image height */
  height?: number | undefined,

  /** image corner radius */
  cornerRadius?: string,


  /** image source url */
  src?: string,

  /** DAM image source url */
  damSrc?: {
    "type": string,
    "title": string,
    "thumbnail": string,
    "originalUrl": string
  },

  /** DAM thumbnail source url */
  damThumbnail?: {
    "type": string,
    "title": string,
    "thumbnail": string,
    "originalUrl": string
  },

  /** blur image source for lazy loading */
  blurURL?: string | undefined,

  /** CSS class to be provided to the Image component in ImageWrapper */
  className?: string,

  overflow?: string,

  /** alt text for the image */
  alt?: string,

  /** Image attribute to prioritize image loading */
  priority?: boolean,

  placeholder?: "blur",

  blurDataURL?: string,

  quality?: number;

  loader?: ImageLoader | undefined,

  fill?: boolean,

  objectFit?: "contain" | "cover",

  dataAos?: string,

  dataAosOnce?: boolean,

  dataAosDuration?: number,

  dataAosDelay?: number,

  dataAosOffset?: number,

  dataAosEasing?: string,

  dataAosAnchorPlacement?: string,

  blurOnLoad?: boolean,

  /** event handlers */
  onClick?: () => void,
  onMouseEnter?: () => void,
  onMouseLeave?: () => void,
}

/**
 * Name: ImageWrapper
 * 
 * Description: a function component that receives image data and renders it on the UI using the Image component provided Next.js
 * 
 * @param param0 object containing props as defined in Props type
 * @returns Image component of Next.js that renders the image passed to it in the props
 */
const ImageWrapper = ({
  defaultImage = "/default-image.jpg",
  src = undefined,
  damSrc = undefined,
  damThumbnail = undefined,
  blurURL = "",
  priority = true,
  width,
  height,
  cornerRadius = '0px',
  quality = 95,
  blurOnLoad = true,
  ...props }: T_Props) => {

  const { isMd } = useResponsive();

  const isInContentfulStudio = (typeof window !== "undefined") && window.self !== window.top && window?.document?.getElementsByClassName("styles-module_tooltipWrapper__kqvmR")?.length > 0;

  const isProbablyInEditorMode = isInContentfulStudio;

  const imageSrc: string = damSrc && damSrc?.originalUrl ? damSrc?.originalUrl : (src || damThumbnail?.thumbnail || damSrc?.thumbnail || "");

  const [imgSrc, setImgSrc] = useState<string | undefined | null>((src && !props?.loader ? srcPath({ src: imageSrc, loader: true }) : srcPath({ src: imageSrc })) as string);
  const [imageLoaded, setImageLoaded] = useState(false);

  // const normalizedSrc = normalizeContentfulUrl( typeof(src) === "string" ? src : src?.thumbnail || src?.originalUrl || "");
  // const normalizedSrc = getMediaUrl({ url: normalizeContentfulUrl(imageSrc) || "", proxy: false });
  const normalizedSrc = normalizeContentfulUrl(imageSrc);
  const blurDataURL = `${normalizedSrc}?fit=thumb&fm=jpg&q=5`;

  // const imageLoader: ImageLoader = ({ src, width, quality }: ImageLoaderProps) => {
  //   // return `https://images.ctfassets.net/${src}?w=${width}&q=${quality || 75}&fm=avif`;
  //   let path = imgSrc?.includes('https:') ? `${imgSrc}` : `https:${imgSrc}`;
  //   // path?.includes('?') ? `${path}&w=${width}&q=${quality || 75}` : `${path}?w=${width}&q=${quality}`;
  //   path?.includes('?') ? `${path}&q=${quality || 75}` : `${path}?q=${quality}`;
  //   return path;
  // }

  useEffect(()=>{
    const mediaScr: string = damSrc && damSrc?.originalUrl ? damSrc?.originalUrl : (src || damThumbnail?.thumbnail || damSrc?.thumbnail || "");
    const finalSrc = (src && !props?.loader ? srcPath({ src: mediaScr, format: "webp", loader: true }) : srcPath({ src: mediaScr, format: "webp" })) as string;
    console.log("ImageWrapper ---- ", {
      src, 
      damSrc, 
      mediaScr, 
      "props?.loader": props?.loader, 
      srcPath: srcPath({ src: mediaScr, format: "webp", loader: true }),
      finalSrc
    })
    setImgSrc(finalSrc);
    // setTimeout(() => {
    //   console.log("ImageWrapper ------ ", damSrc, damSrc?.originalUrl, damThumbnail?.thumbnail, imgSrc)
    // }, 10);
  }, [src, damSrc, damThumbnail])

  const imageLoader: ImageLoader = ({ src, width, quality }: ImageLoaderProps) => {
    let path = imgSrc?.includes('https:') ? `${imgSrc}` : `https:${imgSrc}`;

    // ✅ Add width parameter
    const params = new URLSearchParams();
    if (width) params.append('w', width.toString());
    params.append('q', (quality || 75).toString());

    if (path.includes('?')) {
      path += `&${params.toString()}`;
    } else {
      path += `?${params.toString()}`;
    }

    // if (path.includes('?') && !path.includes('&fm=') && !path.includes('?fm=')) {
    //   path += '&fm=avif';
    // } else if (!path.includes('&fm=') && !path.includes('?fm=')) {
    //   path += '?fm=avif';
    // }

    return path;
  };

  const ImageSkeleton = ({ width, height, className }: { width: number | string, height: number | string, className: string }) => (
    <span
      className={`bg-gray-200 block animate-pulse rounded ${className}`}
      style={{ width: `${width}px`, height: `${height}px` }}
    ></span>
  );

  const IMG = ({iSrc}: {iSrc: string}) => <>
    {/* {height} --- {width} */}
    {/* <pre>{JSON.stringify(props?.style, null, 2)}</pre> */}
    {/* <pre>imgSrc --- {iSrc} --- {src}</pre><br/> */}
    {/* <pre>{src}</pre><br/> */}
    {!imageLoaded && (
      <ImageSkeleton width={width || "100%"} height={height || "100%"} className="absolute inset-0" />
    )}
    <Image
      width={width}
      height={height}
      data-testid="ImageWrapper"
      // src={imgSrc ? getMediaUrl({ url: normalizeContentfulUrl(imgSrc as string), proxy: true }) : ""}
      // src={normalizeContentfulUrl(imgSrc as string)}
      src={iSrc || ""}
      sizes={props?.sizes || "(max-width: 768px) 100vw, 50vw, (max-width: 1200px) 50vw, 33vw"}
      className={classNames(
        `ImageWrapper`,
        props?.className,
        props?.overflow,
        `${!imageLoaded ? 'opacity-0' : 'opacity-100'} transition-opacity duration-300`
      )}
      alt={props?.alt || "img"}
      // priority={priority || false}
      fetchPriority="high"   // ensures LCP optimization
      // loading = 'eager' // {lazy} | {eager}
      style={{ width: width || "100%", "borderRadius": cornerRadius, ...props?.style, height: props?.style?.height || 'auto', objectFit: props?.objectFit || "cover" }}
      onError={() => {
        setImgSrc(defaultImage || `${defImage}`);
      }}
      // blurDataURL={`/_next/image?url=${iSrc}&w=16&q=25`}
      // loader={
      //   props?.loader || imageLoader
      // }
      blurDataURL={blurOnLoad && (blurDataURL || iSrc) ? (
        blurDataURL
        || props?.blurDataURL + "?q=10"
        || iSrc + "?w=10&h=10&fit=thumb&fm=jpg&q=5"
        // || `data:image/svg+xml;base64,${toBase64(iSrc)}`
        // || `data:image/svg+xml;base64,${toBase64(convertImage(700, 475))}`
        // || customImageLoader({src: iSrc, width: width})
      ) : ("")}
      placeholder={blurOnLoad ? props?.placeholder || (width && height && (width > 40 || height > 40) ? "blur" : undefined) : undefined}
      quality={isMd ? quality || 80 : 50}
      // fill={props?.fill}
      {...(()=>!height && !width ? {fill:props?.fill} : {})()}
      onClick={props?.onClick}
      onMouseEnter={props?.onMouseEnter}
      onMouseLeave={props?.onMouseLeave}
      {...(() => (props?.hasOwnProperty("useMap") ? { useMap: props?.useMap } : {}))()}
      {...(() => (props?.dataAos ? { "data-aos": props?.dataAos } : {}))()}
      {...(() => (props?.dataAosOnce ? { "data-aos-once": props?.dataAosOnce } : {}))()}
      {...(() => (props?.dataAosDuration ? { "data-aos-duration": props?.dataAosDuration } : {}))()}
      {...(() => (props?.dataAosDelay ? { "data-aos-delay": props?.dataAosDelay } : {}))()}
      {...(() => (props?.dataAosOffset ? { "data-aos-offset": props?.dataAosOffset } : {}))()}
      {...(() => (props?.dataAosEasing ? { "data-aos-easing": props?.dataAosEasing } : {}))()}
      {...(() => (props?.dataAosAnchorPlacement ? { "data-aos-anchor-placement": props?.dataAosAnchorPlacement } : {}))()}
      onLoad={() => setImageLoaded(true)}
      unoptimized
    />
    {/* {typeof(props?.fill)} -- {JSON.stringify(props?.fill || false)} */}
  </>

  return (
    (damSrc?.originalUrl || src || damThumbnail?.thumbnail) ?
      <span className={classNames("block relative", props.className)}>
        {/* <pre style={{padding: 20, background: "#fff"}}>{JSON.stringify(props, null, 2)}</pre> */}
        <IMG iSrc={imgSrc || ""} />
      </span>
      : (isProbablyInEditorMode ? <span
        className="block relative"
        style={(() => (isProbablyInEditorMode ? { minHeight: props?.style?.height || 'auto' || 200, minWidth: width || "100%", ...props?.style || 200 } : {}))()}
      >
        <IMG iSrc={imgSrc || ""} />
      </span> : null)
  );
};

export default memo(ImageWrapper);
